import {
    actionUpdated,
    actionWaking,
    commChangedState,
    communicationOccurred,
    formSubmitted,
    integrationStateChanged,
    integrationSyncStateChanged,
    timelineChanged,
} from '$state/events';
import { store } from '$state';
import { fetchTimeline, updateTimeline } from '$state/concerns/timeline';
import { listSources } from '$state/concerns/sources';
import * as dentally from '$state/concerns/dentally/patients';
import * as bulk from '$state/concerns/bulk';
import * as patients from '$state/concerns/patient';
import { conversationRead, conversationUnread, patientStarred, patientUnstarred } from '$state/concerns/inbox';
import { fetchCounts } from '$state/concerns/actions';
import toast from 'react-hot-toast';
import { Notification } from '$ui/Notifications/Notification';
import { removeLabel } from '$state/concerns/client';
import {
    CommEventUnion,
    CommEventUnionInbound,
    FormSubmissionPayload,
    Status,
} from '$types';
import {
    CommEventUnion as CommEventUnionV2,
    CommEventUnionInbound as CommEventUnionInboundV2,
    TimelineUpdatedEvent
} from '$types/timeline-v2';
import React from 'react';
import { featureEnabled } from '$state/queries/features';

interface Message {
    identity: string;
    payload: unknown;
}

interface MessageMap {
    [event: string]: (msg: Message) => void;
}

const timelineUpdateEventsEnabled = featureEnabled(
    store.getState(),
    'timeline-update-events',
);

const routes: MessageMap = {
    'patient.communications.changed-state': (msg) => {
        if (!msg.identity) {
            console.error(
                `Received patient.communications.changed-state without patientId`,
            );
            return;
        }
        const payload = msg.payload as CommEventUnion;
        // temporary until timeline v2 is ready - [sc-4334]
        const status: string =
            typeof payload.status === 'object'
                ? (payload.status as Status).name
                : payload.status;

        store.dispatch(
            commChangedState({
                patientId: msg.identity,
                communication: {
                    ...payload,
                    status: status || 'sent',
                },
                state: payload.status,
            }),
        );
    },

    'patient.opportunity.changed': (msg) => {
        const payload = msg.payload as {
            stage: string;
            next_action_at: string;
        };

        store.dispatch(
            actionUpdated({
                patientId: msg.identity,
                stage: payload.stage,
                nextActionAt: payload.next_action_at,
            }),
        );

        store.dispatch(fetchCounts([]));
    },

    'patient.communication.sent': (msg) => {
        const payload = msg.payload as CommEventUnion;
        // temporary until timeline v2 is ready - [sc-4334]
        const status: string =
            typeof payload.status === 'object'
                ? (payload.status as Status).name
                : payload.status;

        store.dispatch(
            communicationOccurred({
                patientId: msg.identity,
                communication: {
                    ...payload,
                    status: status || 'sent',
                } as CommEventUnion,
                inbound: false,
            }),
        );
    },

    'patient.communication.received': (msg) => {
        const payload = msg.payload as
            | CommEventUnionInbound
            | CommEventUnionInboundV2;
        const status: string =
            typeof payload.status === 'object'
                ? (payload.status as Status).name
                : payload.status;

        // updates inbox
        store.dispatch(
            communicationOccurred({
                patientId: msg.identity,
                communication: {
                    ...payload,
                    status: status || 'received',
                } as CommEventUnion | CommEventUnionV2,
                inbound: true,
            }),
        );

        if (!timelineUpdateEventsEnabled) {
            // updates timeline
            store.dispatch(
                timelineChanged({
                    patientId: msg.identity,
                    commId: payload.id,
                }),
            );
        }
    },

    'attachment.saved': (msg) => {
        if (!timelineUpdateEventsEnabled) {
            store.dispatch(
                timelineChanged({
                    patientId: msg.identity,
                    commId: msg.payload as string,
                }),
            );
        }
    },

    'form.submission.applied': (msg) => {
        store.dispatch(formSubmitted(msg.identity));
        if (!timelineUpdateEventsEnabled) {
            store.dispatch(
                timelineChanged({
                    patientId: msg.identity,
                    commId: (msg.payload as FormSubmissionPayload).id,
                }),
            );
        }
    },

    'conversation.read': ({ identity }) => {
        store.dispatch(
            conversationRead({ patientId: identity, notify: false }),
        );
    },

    'conversation.unread': ({ identity }) => {
        store.dispatch(
            conversationUnread({ patientId: identity, notify: false }),
        );
    },

    'patient.starred': ({ identity }) => {
        store.dispatch(
            patientStarred({
                patientId: identity,
                notify: false,
            }),
        );
    },

    'patient.unstarred': ({ identity }) => {
        store.dispatch(
            patientUnstarred({
                patientId: identity,
                notify: false,
            }),
        );
    },

    'patient.labels.cleanup': (msg) => {
        const payload = msg.payload as string;
        store.dispatch(removeLabel(payload));
    },

    'communication.batch.changed-state': (msg) => {
        const payload = msg.payload as string;
        store.dispatch(bulk.load(payload));
    },

    'notification.sms': ({ payload }) => {
        const {
            communication_id: id,
            patient_name: name,
            patient_id: patientId,
            date,
        } = payload as {
            communication_id: string;
            patient_name: string;
            patient_id: string;
            date: string;
        };

        toast.custom(
            ({ visible }) => {
                return React.createElement(Notification, {
                    visible,
                    icon: 'Smartphone',
                    title: name,
                    action: 'New SMS from',
                    time: date,
                    onView: () => {
                        store.dispatch(
                            patients.openPatient({
                                patientId,
                            }),
                        );
                        toast.dismiss(id);
                    },
                    onClose: () => toast.dismiss(id),
                });
            },
            {
                id,
                duration: Infinity,
                position: 'bottom-right',
            },
        );
    },

    'integrations.sync.state.changed': (msg) => {
        const payload = msg.payload as {
            id: string;
            client_id: number;
            integration: string;
            state: 'queued' | 'syncing' | 'failed' | 'complete';
            queued_at: string;
            syncing_at: string | null;
            completed_at: string | null;
            failed_at: string | null;
            error: string | null;
        };

        store.dispatch(integrationSyncStateChanged(payload));
    },

    'integrations.state.changed': (msg) => {
        const payload = msg.payload as {
            client_id: number;
            integration: string;
            state: 'disconnected' | 'healthy' | 'unhealthy';
        };

        store.dispatch(integrationStateChanged(payload));
        const { integration, state } = payload;

        const integrationCap =
            integration.charAt(0).toUpperCase() + integration.slice(1);

        if (state === 'unhealthy') {
            toast.error(
                () => {
                    return React.createElement(
                        'a',
                        {
                            href: '/settings/integrations/' + integration,
                            style: {
                                color: '#fff',
                                'font-weight': 'bold',
                            },
                        },
                        `Your ${integrationCap} integration is unhealthy. Go to ${integrationCap} settings to fix it.`,
                    );
                },
                { id: integration },
            );
        }
    },

    'dentally.patient.synced': (msg) => {
        const payload = msg.payload as { patient_id: string };
        store.dispatch(dentally.get(payload.patient_id));
    },

    'dentally.patient.create.failed': (msg) => {
        const { patient_id: id } = msg.payload as { patient_id: string };

        toast.custom(
            ({ visible }) => {
                return React.createElement(Notification, {
                    visible,
                    icon: 'Link',
                    title: 'Dentally',
                    action: 'Dentally patient creation failed',
                    time: new Date().toString(),
                    onView: () => {
                        store.dispatch(
                            patients.openPatient({
                                patientId: id,
                            }),
                        );
                        toast.dismiss(id);
                    },
                    onClose: () => toast.dismiss(id),
                });
            },
            {
                id,
                duration: Infinity,
                position: 'bottom-right',
            },
        );
    },

    'patient.lead-source.changed': () => {
        store.dispatch(listSources());
    },

    'patient.business-event.created': ({ identity }) => {
        if (!timelineUpdateEventsEnabled) {
            store.dispatch(fetchTimeline(identity));
        }
    },

    'patient.snooze.waking': (msg) => {
        const payload = msg.payload as { stage: string };
        store.dispatch(
            actionWaking({
                patientId: msg.identity,
                stage: payload.stage,
            }),
        );
    },

    'timeline.updated-event': (msg) => {
        if (timelineUpdateEventsEnabled) {
            store.dispatch(updateTimeline({
                event: msg.payload as TimelineUpdatedEvent,
                patientId: msg.identity
            }));
        }
    },
};

export default routes;
